import React, { useState, useEffect, useMemo, useRef } from 'react';

import { Plus, X, RotateCcw, Package } from 'lucide-react';

import { vehicleLabel } from './Vehicles.jsx';

import { START_HOUR, END_HOUR, HOURS_SHOWN, PX_PER_HOUR, SNAP_MINUTES, DEFAULT_DURATION, DAY_NAMES_SHORT, startOfDay, startOfWeek, addDays, isSameDay, formatTimeOfDay, hourLabel } from './lib/calendar-view.js';
function DayView({ date, jobs, customers, vehicles, onSlotTap, onBlockTap }) {
  const gridRef = useRef(null);
  const customerById = useMemo(
    () => Object.fromEntries(customers.map((c) => [c.id, c])),
    [customers]
  );
  const vehicleById = useMemo(
    () => Object.fromEntries(vehicles.map((v) => [v.id, v])),
    [vehicles]
  );

  const dayJobs = useMemo(
    () => jobs.filter((j) => isSameDay(j._start, date)),
    [jobs, date]
  );

  function handleGridClick(e) {
    if (!gridRef.current) return;
    // Block clicks bubble up; ignore if the click was on a block
    if (e.target.closest('.cal-block')) return;

    const rect = gridRef.current.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const hoursPast = y / PX_PER_HOUR;
    const totalMinutes = (START_HOUR * 60) + Math.round((hoursPast * 60) / SNAP_MINUTES) * SNAP_MINUTES;
    const clamped = Math.max(START_HOUR * 60, Math.min(END_HOUR * 60 - SNAP_MINUTES, totalMinutes));
    const start = new Date(date);
    start.setHours(Math.floor(clamped / 60), clamped % 60, 0, 0);
    onSlotTap(start, DEFAULT_DURATION);
  }

  return (
    <div className="cal-day">
      <div className="cal-day-grid" ref={gridRef} onClick={handleGridClick}>
        {/* Hour rows (background) */}
        {Array.from({ length: HOURS_SHOWN }, (_, i) => {
          const h = START_HOUR + i;
          return (
            <div key={h} className="cal-hour-row" style={{ height: PX_PER_HOUR }}>
              <div className="cal-hour-label">{hourLabel(h)}</div>
              <div className="cal-hour-cell" />
            </div>
          );
        })}

        {/* Job blocks (absolutely positioned over the grid) */}
        {dayJobs.map((j) => {
          const startMinFromGridTop = (j._start.getHours() - START_HOUR) * 60 + j._start.getMinutes();
          const top = (startMinFromGridTop / 60) * PX_PER_HOUR;
          const height = Math.max(20, (j._durationMinutes / 60) * PX_PER_HOUR);
          const customer = customerById[j.customerId];
          const vehicle = vehicleById[j.vehicleId];
          const isReturn = j._visitKind === 'return' || j._visitKind === 'parts-arrival';
          const ReturnIcon = j._visitKind === 'parts-arrival' ? Package : RotateCcw;
          return (
            <button
              key={j._blockKey || j.id}
              type="button"
              className={`cal-block badge-${j.status || 'draft'} ${isReturn ? 'is-return' : ''}`}
              style={{ top, height }}
              onClick={(e) => { e.stopPropagation(); onBlockTap(j); }}
            >
              {isReturn && (
                <span className="cal-block-return-tag" title={j._visitKind === 'parts-arrival' ? 'Parts arrival' : 'Return visit'}>
                  <ReturnIcon size={10} />
                </span>
              )}
              <span className="cal-block-time">
                {formatTimeOfDay(j._start)} · {j._durationMinutes}m
              </span>
              <span className="cal-block-name">{customer?.name || '(deleted customer)'}</span>
              <span className="cal-block-desc">{j.description}</span>
              {vehicle && <span className="cal-block-vehicle">{vehicleLabel(vehicle)}</span>}
            </button>
          );
        })}
      </div>
      {dayJobs.length === 0 && (
        <p className="muted small" style={{ textAlign: 'center', marginTop: 8 }}>
          Tap an empty time slot to schedule an appointment.
        </p>
      )}
    </div>
  );
}

// ─── Month view ───────────────────────────────────────────────────────────
function MonthView({ monthStart, jobs, customers, onDayTap }) {
  // Build a 6-row × 7-col grid starting from the Sunday on or before the 1st.
  const gridStart = startOfWeek(monthStart);
  const cells = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
  const today = startOfDay(new Date());
  const customerById = useMemo(
    () => Object.fromEntries(customers.map((c) => [c.id, c])),
    [customers]
  );

  return (
    <div className="cal-month">
      <div className="cal-month-dow-row">
        {DAY_NAMES_SHORT.map((n) => (
          <div key={n} className="cal-month-dow">{n}</div>
        ))}
      </div>
      <div className="cal-month-grid">
        {cells.map((d) => {
          const inMonth = d.getMonth() === monthStart.getMonth();
          const isToday = isSameDay(d, today);
          const dayJobs = jobs.filter((j) => isSameDay(j._start, d)).sort((a, b) => a._start - b._start);
          return (
            <button
              type="button"
              key={d.toISOString()}
              className={`cal-month-cell ${inMonth ? '' : 'out'} ${isToday ? 'today' : ''}`}
              onClick={() => onDayTap(d)}
            >
              <div className="cal-month-num">{d.getDate()}</div>
              <div className="cal-month-jobs">
                {dayJobs.slice(0, 3).map((j) => {
                  const isReturn = j._visitKind === 'return' || j._visitKind === 'parts-arrival';
                  return (
                    <span
                      key={j._blockKey || j.id}
                      className={`cal-month-dot badge-${j.status || 'draft'} ${isReturn ? 'is-return' : ''}`}
                      title={isReturn ? (j._visitKind === 'parts-arrival' ? 'Parts arrival visit' : 'Return visit') : undefined}
                    >
                      {isReturn && '↩ '}
                      {formatTimeOfDay(j._start)} {customerById[j.customerId]?.name || ''}
                    </span>
                  );
                })}
                {dayJobs.length > 3 && (
                  <span className="cal-month-more">+{dayJobs.length - 3} more</span>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Searchable customer picker (combobox) ───────────────────────────────
// onCreateNew (optional): when provided, the dropdown shows a "+ New
// customer" item at the top. Clicked → calls onCreateNew(query) so the
// parent can launch the CustomerWizard with the current search text as
// a pre-fill.
function CustomerPicker({ customers, value, onChange, onCreateNew }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef(null);

  const selectedCustomer = customers.find((c) => c.id === value);
  // When the dropdown is open, the input shows the live search query.
  // When closed, it shows the selected customer's name (or empty).
  const inputValue = open ? query : (selectedCustomer?.name || '');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return customers;
    const qDigits = query.replace(/\D/g, '');
    return customers.filter((c) => {
      if (c.name?.toLowerCase().includes(q)) return true;
      if (qDigits && String(c.phone || '').replace(/\D/g, '').includes(qDigits)) return true;
      return false;
    });
  }, [customers, query]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function onDocMouseDown(e) {
      if (!containerRef.current?.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  function handleFocus() {
    setQuery('');
    setOpen(true);
  }
  function handleSelect(c) {
    onChange(c.id);
    setQuery('');
    setOpen(false);
  }
  function handleClear(e) {
    e.stopPropagation();
    onChange('');
    setQuery('');
    setOpen(true);
  }

  return (
    <div className="picker" ref={containerRef}>
      <input
        type="text"
        className="picker-input"
        value={inputValue}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={handleFocus}
        placeholder="Search customers…"
        autoComplete="off"
      />
      {value && !open && (
        <button
          type="button"
          className="picker-clear"
          onClick={handleClear}
          aria-label="Clear selection"
        >
          <X size={14} />
        </button>
      )}
      {open && (
        <ul className="picker-dropdown">
          {onCreateNew && (
            <li className="picker-create-new">
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  const q = query;
                  setQuery('');
                  setOpen(false);
                  onCreateNew(q);
                }}
              >
                <Plus size={14} strokeWidth={2.5} />
                <span className="picker-name">
                  + New customer{query.trim() ? ` "${query.trim()}"` : ''}
                </span>
              </button>
            </li>
          )}
          {filtered.length === 0 ? (
            !onCreateNew && <li className="picker-empty">No matches</li>
          ) : (
            filtered.map((c) => (
              <li key={c.id}>
                <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => handleSelect(c)}>
                  <span className="picker-name">{c.name}</span>
                  {c.phone && <span className="picker-sub">{c.phone}</span>}
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}

// ─── Slide-in sidebar form ────────────────────────────────────────────────

export { DayView, MonthView, CustomerPicker };
